import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Subscription } from 'rxjs';
import { Messages } from 'src/app/constants/messages';
import {
  BlobUploadResponse,
  KnowledgeBaseBlobResponse,
  KnowledgeBaseConfigurationAttribute,
  KnowledgeBaseConfigurationAttributeFileSizeFormat,
  KnowledgeBaseContent,
  KnowledgeBaseCreateContent,
  KnowledgeBaseDetailsPayloadResponse,
  KnowledgeBasePayloadResponse
} from 'src/app/model';
import { ServicecallService } from 'src/app/services/servicecall.service';
import { AlertDialogComponent } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import { FileTypeService } from '../services/file-type/file-type.service';

@Component({
  selector: 'app-upload-modal',
  templateUrl: './upload-modal.component.html',
  styleUrls: ['./upload-modal.component.css']
})
export class UploadModalComponent implements OnInit, OnDestroy {

  @Input() formId: string;

  // Form  
  uploadForm: FormGroup;
  selectedIndex: number = 0;
  isUploadFormSubmitted: boolean = false;
  startFileBlobUpload: boolean = false;
  startThumbnailBlobUpload: boolean = false;
  fileContent: Partial<KnowledgeBaseBlobResponse> = {
    file: null,
    thumbnail: null
  };
  maxFileSize: number;
  maxThumbnailSize: number;

  // Dropdown
  areaList: string[] = [];
  categoryList: string[] = [];

   // Miscellaneous
   errorMessage: string;
   fileMimeTypes = this._fileTypeService.FILE_MIME_TYPES;
   thumbnailMimeTypes = this._fileTypeService.THUMBNAIL_MIME_TYPES;
   readonly Messages = Messages;

  // Form  
  private _formContent: KnowledgeBaseContent;
  private _isAllFilesSelected: boolean = false;
  private readonly TWO = 2;
  private _formStatus = Messages.ADD;
  private _fileSelectedCount = 0;
  private _fileUploadedCount = 0;
  private _blobResponse: Partial<KnowledgeBaseBlobResponse> = {
    file: null,
    thumbnail: null
  };

  get fileSelectedCount() {
    return this._fileSelectedCount;
  }

  set fileSelectedCount(value: number) {
    this._fileSelectedCount = value;
    this._isAllFilesSelected = this.fileSelectedCount === this.TWO ? true : false;
  }

  // Subscription
  private _dialogRefAfterClosedSubscription: Subscription;
  private _knowledgeBaseDetailsSubscription: Subscription;
  private _updateKnowledgeBaseSubscription: Subscription;
  private _createKnowledgeBaseSubscription: Subscription;
  private _dialogRefAfterClosedModalSubscription: Subscription;

  
  // configurationsInfoSubscription: Subscription;

  constructor(
    private _activeModal: NgbActiveModal,
    private _dialog: MatDialog,
    private _fb: FormBuilder,
    private _service: ServicecallService,
    private _fileTypeService: FileTypeService
  ) {
    this._createForm();
  }

  ngOnInit(): void {
    this._loadFormData();
    this._getConfigurationsInfo();
    this._formStatus = this.formId ? Messages.EDIT : Messages.ADD;
  }

  closeModal() {
    if (!this.isUploadFormSubmitted && this.uploadForm.dirty) {
      const dialogRef = this._dialog.open(
        AlertDialogComponent,
        {
          data: { msg: Messages.UNSAVED_CHANGES, type: null },
          panelClass: 'custom-alert-dialog',
          disableClose: true
        }
      );
      this._dialogRefAfterClosedModalSubscription = dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this._closeModalActions();
        }
      });
    } else {
      this._closeModalActions();
    }
  }

  onTabChange(event: any) {
    this.selectedIndex = event.index;
  }

  onStepChange(changeType: 'next' | 'previous') {
    if (changeType === 'next' && this.selectedIndex < 5) {
      this.selectedIndex = this.selectedIndex + 1;
    } else if (changeType === 'previous' && this.selectedIndex > -1) {
      this.selectedIndex = this.selectedIndex - 1;
    }
  }

  onFormSubmit() {
    this.isUploadFormSubmitted = true;
    if (this.uploadForm.invalid) {
      return;
    }

    // Form status check
    if (this._formStatus === Messages.ADD) {
      this.startFileBlobUpload = this._blobResponse?.file ? true : false;
      this.startThumbnailBlobUpload = this._blobResponse?.thumbnail ? true : false;
    } else if (this._formStatus === Messages.EDIT) {
      const dialogRef = this._dialog.open(
        AlertDialogComponent,
        {
          data: { msg: Messages.SUBMIT_UNSAVED_CHANGES, type: null },
          panelClass: 'custom-alert-dialog',
          disableClose: true
        }
      );
      this._dialogRefAfterClosedSubscription = dialogRef.afterClosed().subscribe(
        (dialogResponse) => {
          if (dialogResponse) {
            if (this._blobResponse?.file || this._blobResponse?.thumbnail) {
              this.startFileBlobUpload = this._blobResponse?.file ? true : false;
              this.startThumbnailBlobUpload = this._blobResponse?.thumbnail ? true : false;
            } else {
              // If there is no file or thumbnail update              
              this._updateFormData();
            }
          }
        }
      );
    }
  }

  uploadResponse(event: Partial<BlobUploadResponse>, formControlName: string) {
    this._blobResponse[formControlName] = event;
    if (event?.stage === 'upload') {
      this._updateFileUploadedCount(event?.status, false);
      this.uploadForm.patchValue({
        [formControlName]: event?.fileUrl
      });
      if (this.fileSelectedCount === this._fileUploadedCount) {
        if (this.formId) {
          this._updateFormData();
        } else {
          this._saveFormData();
        }
      }
    } else if (event?.stage === 'select') {
      this._updateFileSelectedCount(event?.status);
    } else if (event?.stage === 'exist-delete') {
      this._updateFileSelectedCount(!event?.status);
      this._updateFileUploadedCount(!event?.status);
    } else if (event?.stage === 'soft-delete') {
      this._updateFileSelectedCount(!event?.status);
    }
    if (formControlName !== 'thumbnail') {
      this.uploadForm.patchValue({
        estimatedTime: event.fileDuration
      });
    }
  }

  ngOnDestroy(): void {
    if (this._dialogRefAfterClosedSubscription) {
      this._dialogRefAfterClosedSubscription.unsubscribe();
    }
    if (this._dialogRefAfterClosedModalSubscription) {
      this._dialogRefAfterClosedModalSubscription.unsubscribe();
    }
    if (this._knowledgeBaseDetailsSubscription) {
      this._knowledgeBaseDetailsSubscription.unsubscribe();
    }
    if (this._updateKnowledgeBaseSubscription) {
      this._updateKnowledgeBaseSubscription.unsubscribe();
    }
    if (this._createKnowledgeBaseSubscription) {
      this._createKnowledgeBaseSubscription.unsubscribe();
    }
    // if (this.configurationsInfoSubscription) {
    //   this.configurationsInfoSubscription.unsubscribe();
    // }
  }

  private _updateFileSelectedCount(condition: boolean | string, hasDecrement: boolean = true) {
    condition ? this.fileSelectedCount++ : (hasDecrement ? this.fileSelectedCount-- : this.fileSelectedCount);
  }

  private _updateFileUploadedCount(condition: boolean | string, hasDecrement: boolean = true) {
    condition ? this._fileUploadedCount++ : (hasDecrement ? this._fileUploadedCount-- : this._fileUploadedCount);
  }

  private _createForm() {
    this.uploadForm = this._fb.group({
      title: ["", [Validators.required]],
      description: [""],
      area: ["", [Validators.required]],
      category: ["", [Validators.required]],
      file: ["", [Validators.required]],
      thumbnail: [""],
      hashtag: [""],
      author: [""],
      estimatedTime: [{ value: '', disabled: true }]
    });
  }

  private _loadFormData() {
    if (this.formId) {
      this._knowledgeBaseDetailsSubscription = this._service.getKnowledgeBaseDetails({ knowledegeBaseId: this.formId }).subscribe(
        (result: KnowledgeBaseDetailsPayloadResponse) => {
          this._formContent = result?.response?.data[0];
          this.uploadForm.patchValue({
            title: this._formContent?.title,
            description: this._formContent?.description,
            area: this._formContent?.area,
            category: this._formContent?.category,
            file: this._formContent?.fileDetails?.file?.url,
            thumbnail: this._formContent?.fileDetails?.thumbnail?.url,
            hashtag: this._formContent?.details?.hashtag?.join(' '),
            author: this._formContent?.author,
            estimatedTime: this._formContent?.estimatedTime ? this._formContent?.estimatedTime : 'NA'
          });
          // For file
          this._updateFileSelectedCount(this._formContent?.fileDetails?.file?.url, false);
          this._updateFileUploadedCount(this._formContent?.fileDetails?.file?.url, false);
          // For thumbnail
          this._updateFileSelectedCount(this._formContent?.fileDetails?.thumbnail?.url, false);
          this._updateFileUploadedCount(this._formContent?.fileDetails?.thumbnail?.url, false);
          // Set file contents
          this._setFileContent();
        }
      );
    }
  }

  private _updateFormData() {
    const payloadContent = this._setPayloadContent();
    this._updateKnowledgeBaseSubscription = this._service.updateKnowledgeBase(
      {
        knowledgeBaseDetails: payloadContent,
        knowledgeBaseId: this.formId
      }
    )
      .subscribe(
        (result: KnowledgeBasePayloadResponse) => {
          if (result.status) {
            if (result.response) {
              this._service.toasterService({ type: 'success', msg: result?.response?.message });
              this._activeModal.close(true);
            } else {
              this.errorMessage = result?.response?.message;
            }
          } else {
            this.errorMessage = result?.response?.message;
          }
        }
      );
  }

  private _saveFormData() {
    const payloadContent = this._setPayloadContent();
    this._createKnowledgeBaseSubscription = this._service.createKnowledgeBase(
      {
        knowledgeBaseDetails: payloadContent
      }
    )
      .subscribe(
        (result: KnowledgeBasePayloadResponse) => {
          if (result.status) {
            if (result.response) {
              this._service.toasterService({ type: 'success', msg: result?.response?.message });
              this._activeModal.close(true);
            } else {
              this.errorMessage = result?.response?.message;
            }
          } else {
            this.errorMessage = result?.response?.message;
          }
        }
      );
  }

  private _setPayloadContent() {
    this._setFileContent();
    const payloadContent: KnowledgeBaseCreateContent = {
      siteId: this._service.getSiteId(),
      title: this.uploadForm.get('title').value,
      description: this.uploadForm.get('description').value,
      area: this.uploadForm.get('area').value,
      category: this.uploadForm.get('category').value,
      fileDetails: {
        file: {
          url: this.fileContent?.file?.fileUrl,
          type: this.fileContent?.file?.fileType,
          size: this.fileContent?.file?.fileSize,
          name: this.fileContent?.file?.fileName
        },
        thumbnail: {
          url: this.fileContent?.thumbnail?.fileUrl,
          type: this.fileContent?.thumbnail?.fileType,
          size: this.fileContent?.thumbnail?.fileSize,
          name: this.fileContent?.thumbnail?.fileName
        }
      },
      details: {
        comments: null,
        hashtag: this._getTagList(this.uploadForm.get('hashtag').value),
        totalViews: 0,
        totalLikes: 0,
        starRating: {
          rating: 0,
          headCount: 0
        }
      },
      author: this.uploadForm.get('author').value,
      estimatedTime: this.uploadForm.get('estimatedTime').value,
    }
    return payloadContent;
  }

  private _setFileContent() {
    this.fileContent = {
      file: {
        fileUrl: this.uploadForm.get('file').value,
        fileName: this.uploadForm.get('file').value ? (this._blobResponse?.file?.fileName ? this._blobResponse?.file?.fileName : this._formContent?.fileDetails?.file?.name) : null,
        fileSize: this.uploadForm.get('file').value ? (this._blobResponse?.file?.fileSize ? this._blobResponse?.file?.fileSize : +this._formContent?.fileDetails?.file?.size) : null,
        fileType: this._fileTypeService.getFileExtensionFromUrl(this.uploadForm.get('file').value)
      },
      thumbnail: {
        fileUrl: this.uploadForm.get('thumbnail').value,
        fileName: this.uploadForm.get('thumbnail').value ? (this._blobResponse?.thumbnail?.fileName ? this._blobResponse?.thumbnail?.fileName : this._formContent?.fileDetails?.thumbnail?.name) : null,
        fileSize: this.uploadForm.get('thumbnail').value ? (this._blobResponse?.thumbnail?.fileSize ? this._blobResponse?.thumbnail?.fileSize : +this._formContent?.fileDetails?.thumbnail?.size) : null,
        fileType: this._fileTypeService.getFileExtensionFromUrl(this.uploadForm.get('thumbnail').value)
      }
    }
  }

  private _closeModalActions() {
    this._activeModal.close();
    this.uploadForm.reset();
  }

  private _getConfigurationsInfo() {
    // this.configurationsInfoSubscription = this._service.configurationsInfo(this.attributes).subscribe(
    //   (configResponse: KnowledgeBaseConfigurationPayloadResponse) => {
    //     if (configResponse?.status && configResponse?.response?.length) {
    //       // Dropdown values
    //       this.areaList = configResponse?.response?.find(values => values.attribute === 'kba')?.values;
    //       this.categoryList = configResponse?.response?.find(values => values.attribute === 'kbc')?.values;
    //       // File size
    //       const fileSizeFormat: KnowledgeBaseConfigurationAttributeFileSizeFormat = configResponse?.response?.find(values => values.attribute === 'fileSizeFormat')?.values;
    //       this.maxFileSize = fileSizeFormat?.fileSizeFormat?.file;
    //       this.maxThumbnailSize = fileSizeFormat?.fileSizeFormat?.thumbnail;
    //     }
    //   }
    // );
    const areaConfigResponse: KnowledgeBaseConfigurationAttribute = this._service.getConfiguration('kba') ? this._service.getConfiguration('kba', 'object') : {};
    const categoryConfigResponse: KnowledgeBaseConfigurationAttribute = this._service.getConfiguration('kbc') ? this._service.getConfiguration('kbc', 'object') : {};
    // Dropdown values
    this.areaList = areaConfigResponse?.values;
    this.categoryList = categoryConfigResponse?.values;
    // File size
    const fileSizeFormat: KnowledgeBaseConfigurationAttributeFileSizeFormat = this._service.getConfiguration('fileSizeFormat') ? this._service.getConfiguration('fileSizeFormat') : [];
    this.maxFileSize = fileSizeFormat?.fileSizeFormat?.file;
    this.maxThumbnailSize = fileSizeFormat?.fileSizeFormat?.thumbnail;
  }

  private _getTagList(value: string) {
    let list = [];
    if (value) {
      list = value.split(/\s+/);
    }
    return list;
  }
}
